import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Table,TableHeader,TableRow,TableHead,TableBody,TableCell } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { ShoppingCart, ChartColumnStacked } from "lucide-react";
import { useCart } from "modules/cart/hooks/useCart";
import { PurchaseData } from "modules/main/types/user-dashboard";
import React from "react";
import { forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import Amount from "utils/amount";

export interface OrderHistoryProps extends React.HTMLAttributes<HTMLDivElement> {
    lastPurchases:PurchaseData[]
    isDesktop?:boolean
    setOpenCartSheet: (open: boolean) => void;
}

const statusClasses: { [key: string]: string } = {
        "AC":"",
        "CO":"good",
        "CN":"bad"
    }

export const OrderHistoryTable = forwardRef<HTMLDivElement,OrderHistoryProps>(({...props},ref)=>{
    const {clearCart, refreshCart} = useCart();
    const addToCartHandler = (products:string) => {
        clearCart();
        refreshCart(JSON.parse(products));
        props.setOpenCartSheet(true);
    }

    const navigate = useNavigate();
    const handleViewClick = (id:number) => {
        navigate(`/cart/results/${id}`);
    }

    return (
            <Table className="w-full">
                <TableHeader>
                    <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Fecha</TableHead>
                        <TableHead className="text-left">Resumen</TableHead>
                        <TableHead className="text-center">Estado</TableHead>
                        <TableHead className="text-center"></TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {
                        props.lastPurchases.map((purchase,index) => (                            
                            <TableRow key={index} 
                                    className={
                                        cn("",{
                                            "cursor-pointer":purchase.status_code === "CO",
                                        })
                                    }                                 
                                >
                                <TableCell>#{purchase.id}</TableCell>
                                <TableCell className="text-muted-foreground">{new Date(purchase.date).toLocaleDateString('es-CL')}</TableCell>
                                <TableCell  className="text-left text-muted-foreground">
                                    { (purchase.status_code==="PR" || purchase.status_code==="CO")? 
                                        <span className={purchase.status_code==="PR"?"animate-pulse":""}>{(purchase.status_code==="PR")?"Optimizando...":"Resultados disponibles"}
                                        </span>: 
                                    (
                                        (purchase.scenarios > 1)?
                                        (
                                            <>
                                                <p className="text-foreground font-semibold">{purchase.scenarios} escenarios</p>
                                                <p>Desde <Amount
                                                    amount={purchase.total} 
                                                    currency="CLP" 
                                                    locale="es-CL" 
                                                    className="font-semibold text-foreground"
                                                    /> en {purchase.stores} tiendas                                    
                                                </p>
                                            </>                                            
                                        ):(
                                    <>
                                        <p className="text-foreground font-semibold"></p>
                                        <p><Amount
                                            amount={purchase.total} 
                                            currency="CLP" 
                                            locale="es-CL" 
                                            className="font-semibold text-foreground"
                                            /> en {purchase.stores} tiendas                                    
                                        </p>
                                    </>                                            
                                        )
                                    )}
                                </TableCell>
                                <TableCell className="text-center">                                    
                                    <Badge
                                        variant={purchase.status_code === "AC" ? "outline" : "default"}
                                        className={cn(statusClasses[purchase.status_code],"w-24 flex justify-center mx-auto")}>{purchase.status}</Badge>
                                </TableCell>
                                <TableCell className="flex flex-row gap-2">
                                    <div className="w-28">
                                        <Button variant={"outline"} onClick={
                                            () => addToCartHandler(purchase.products)
                                        } className="w-full" >
                                            <ShoppingCart className="w-6 h-6"/>
                                            Repetir
                                        </Button>
                                    </div>
                                    <div className="w-28">
                                        <Button variant={"default"} disabled={(purchase.status_code !== "CO" && purchase.status_code !== "AC" )}
                                            className="w-full"       
                                            onClick={() => handleViewClick(purchase.id)}                                 
                                        >
                                            <ChartColumnStacked className="w-6 h-6" />
                                            Ver
                                        </Button>  
                                    </div>                                  
                                </TableCell>
                            </TableRow>
                        ))
                    }
                </TableBody>
            </Table>
    );
});

export const OrderHistoryCard = forwardRef<HTMLDivElement,OrderHistoryProps>(({...props},ref)=>{    
    const {clearCart, refreshCart} = useCart();
    const navigate = useNavigate();
    const addToCartHandler = (products:string) => {
        clearCart();
        refreshCart(JSON.parse(products));
        props.setOpenCartSheet(true);
    }    
    const handleViewClick = (id:number) => {
        navigate(`/cart/results/${id}`);
    }

    return (
    <div className="flex flex-col gap-4">
        {
            props.lastPurchases.map((purchase,index) => (
        <React.Fragment key={index}>
            <div className="flex flex-col gap-3 w-full flex-1">
                <div className="bg-slate-100 p-2 text-center text-muted-foreground">
                    Pedido <span className="font-semibold">#{purchase.id}</span> del {new Date(purchase.date).toLocaleDateString('es-CL')}
                </div>
                <div className="flex transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted w-full">
                    <div className="min-w-[30%] text-muted-foreground">Estado:</div>
                    <div className="font-semibold">
                        <Badge
                            variant={purchase.status_code === "AC" ? "outline" : "default"}
                            className={cn(statusClasses[purchase.status_code],"w-24 flex justify-center mx-auto")}>{purchase.status}</Badge>
                    </div>
                </div>
                <div className="flex transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted w-full">
                    <div className="min-w-[30%] text-muted-foreground">Resumen:</div>
                    <div>
                        { (purchase.status_code==="PR" || purchase.status_code==="CO")? 
                            <span className={purchase.status_code==="PR"?"animate-pulse":""}>{(purchase.status_code==="PR")?"Optimizando...":"Resultados disponibles"}
                            </span>: 
                        (
                            (purchase.scenarios > 1)?
                            (
                                <>
                                    <p className="text-foreground font-semibold">{purchase.scenarios} escenarios</p>
                                    <p>Desde <Amount
                                        amount={purchase.total} 
                                        currency="CLP" 
                                        locale="es-CL" 
                                        className="font-semibold text-foreground"
                                        /> en {purchase.stores} tiendas                                    
                                    </p>
                                </>                                            
                            ):(
                        <>
                            <p className="text-foreground font-semibold"></p>
                            <p><Amount
                                amount={purchase.total} 
                                currency="CLP" 
                                locale="es-CL" 
                                className="font-semibold text-foreground"
                                /> en {purchase.stores} tiendas                                    
                            </p>
                        </>                                            
                            )
                        )}

                    </div>
                </div>           
                <div className="flex gap-6 justify-center transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted w-full">
                    <div className="w-28">
                        <Button variant={"outline"} onClick={
                            () => addToCartHandler(purchase.products)
                        } className="w-full" >
                            <ShoppingCart className="w-6 h-6"/>
                            Repetir
                        </Button>
                    </div>
                    <div className="w-28">
                        <Button variant={"default"} disabled={(purchase.status_code !== "CO" && purchase.status_code !== "AC" )}
                            className="w-full"       
                            onClick={() => handleViewClick(purchase.id)}                                 
                        >
                            <ChartColumnStacked className="w-6 h-6" />
                            Ver
                        </Button>  
                    </div>
                </div>                        
            </div>     
            <Separator/>
        </React.Fragment>              
        ))
        }

    </div>
    );
});
